import { CharacterClass } from "../models/Character.ts"

function toSentenceCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

function toTitleCase(str: string): string {
    return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
}

function toPossessiveCase(str: string): string {
    return str.endsWith("s") ? `${str}'` : `${str}'s`
}

interface ConvertMillisecondsOptions {
    commaSeparated?: boolean
    useFullWords?: boolean
    onlyIncludeLargest?: boolean
    largestCount?: number
    nonBreakingSpace?: boolean
}

function convertMillisecondsToPrettyString(
    {
        millis,
        commaSeparated = false,
        useFullWords = false,
        onlyIncludeLargest = false,
        largestCount = 1,
        nonBreakingSpace = false,
    }: { millis: number } & ConvertMillisecondsOptions = {
        millis: 0,
    }
): string {
    if (millis == 0) {
        return "0 seconds"
    } else if (millis < 0) {
        return "in the past"
    }

    const seconds = Math.floor(millis / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)
    const months = Math.floor(weeks / 4)
    const years = Math.floor(months / 12)

    const resultArray: string[] = []
    if (years > 0)
        resultArray.push(
            `${years} ${pluralize("year", years)}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (months % 12 > 0)
        resultArray.push(
            `${months % 12} ${pluralize("month", months % 12)}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (weeks % 4 > 0)
        resultArray.push(
            `${weeks % 4} ${pluralize("week", weeks % 4)}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (days % 7 > 0)
        resultArray.push(
            `${days % 7} ${pluralize("day", days % 7)}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (hours % 24 > 0)
        resultArray.push(
            `${hours % 24} ${pluralize("hour", hours % 24)}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (minutes % 60 > 0 && hours < 12)
        resultArray.push(
            `${minutes % 60} min${useFullWords ? "ute" : ""}${minutes % 60 > 1 ? "s" : ""}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
    if (seconds % 60 > 0 && minutes < 30 && hours === 0)
        resultArray.push(
            `${seconds % 60} sec${useFullWords ? "ond" : ""}${seconds % 60 > 1 ? "s" : ""}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )

    if (onlyIncludeLargest) {
        return resultArray.length > 0
            ? resultArray
                  .slice(0, largestCount)
                  .join(commaSeparated ? ", " : " ")
            : "0 seconds"
    }
    return resultArray.join(commaSeparated ? ", " : " ").trim()
}

function mapClassesToString(
    classes?: CharacterClass[],
    nonBreakingSpace: boolean = false
): string {
    const excludedClasses = ["Epic", "Legendary"]

    if (!classes) return ""
    return classes
        .filter(
            (characterClass) => !excludedClasses.includes(characterClass.name)
        )
        .map((characterClass) =>
            `${characterClass.name} ${characterClass.level}`.replace(
                / /g,
                nonBreakingSpace ? "\u00A0" : " "
            )
        )
        .join(", ")
}

function wrapText(
    text: string,
    width: number,
    font: string,
    context: CanvasRenderingContext2D,
    maxLines?: number
) {
    if (!text || text.length === 0) return [""]

    const previousFont = context.font
    context.font = font

    const words = text.split(" ")
    let line = ""
    let lines: string[] = []

    for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " "
        const testWidth = context.measureText(testLine).width
        if (testWidth > width) {
            const wordWidth = context.measureText(words[i]).width
            lines.push(line ? line.trim() : "")
            if (wordWidth > width) {
                const letters = words[i].split("")
                let partialWord = ""
                for (let j = 0; j < letters.length; j++) {
                    const testPartialWord = partialWord + letters[j]
                    const testPartialWidth =
                        context.measureText(testPartialWord).width
                    if (testPartialWidth > width) {
                        lines.push(partialWord.slice(0, -3) + "...")
                        partialWord = ""
                    }
                    partialWord += letters[j]
                }
            } else {
                line = words[i] + " "
            }
        } else {
            line = testLine
        }
    }

    lines.push(line)
    if (maxLines && lines.length > maxLines) {
        lines = lines.slice(0, maxLines)
        const originalLastLine = lines[maxLines - 1]
        const letters = originalLastLine.split("")
        let testLine = ""
        for (let i = 0; i < letters.length; i++) {
            testLine += letters[i]
            if (context.measureText(testLine).width > width) {
                lines[maxLines - 1] = testLine.slice(0, -3)
                break
            }
        }
    }

    context.font = previousFont

    return lines.filter((line) => line.length > 0).map((line) => line.trim())
}

function truncateText(
    text: string,
    width: number,
    font: string,
    context: CanvasRenderingContext2D
) {
    const previousFont = context.font
    context.font = font

    // Fast path: text fits without truncation
    if (context.measureText(text).width <= width) {
        context.font = previousFont
        return text
    }

    const ellipsisWidth = context.measureText("...").width
    const targetWidth = width - ellipsisWidth

    if (targetWidth <= 0) {
        context.font = previousFont
        return ""
    }

    // Binary search for the longest substring that fits
    let lo = 0
    let hi = text.length
    while (lo < hi) {
        const mid = (lo + hi + 1) >>> 1
        if (context.measureText(text.slice(0, mid)).width <= targetWidth) {
            lo = mid
        } else {
            hi = mid - 1
        }
    }

    context.font = previousFont
    if (lo === text.length) return text
    return text.slice(0, lo).trim() + "..."
}

function getTextSize(
    text: string,
    font: string,
    context: CanvasRenderingContext2D
): { width: number; height: number } {
    const previousFont = context.font
    context.font = font
    const metrics = context.measureText(text)
    const width = metrics.width
    const height =
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
    context.font = previousFont
    return { width, height }
}

function pluralize(word: string, count: number): string {
    if (count === 1) return word
    if (word.endsWith("y") && !word.endsWith("ay") && !word.endsWith("ey")) {
        return word.slice(0, -1) + "ies"
    }
    if (word.endsWith("s") || word.endsWith("x") || word.endsWith("z")) {
        return word + "es"
    }
    return word + "s"
}

function levenshteinDistance(s: string, t: string): number {
    if (!s.length) return t.length
    if (!t.length) return s.length
    const arr: number[][] = []
    for (let i = 0; i <= t.length; i++) {
        arr[i] = [i]
        for (let j = 1; j <= s.length; j++) {
            arr[i][j] =
                i === 0
                    ? j
                    : Math.min(
                          arr[i - 1][j] + 1,
                          arr[i][j - 1] + 1,
                          arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1)
                      )
        }
    }
    return arr[t.length][s.length]
}

export {
    toSentenceCase,
    toPossessiveCase,
    convertMillisecondsToPrettyString,
    mapClassesToString,
    wrapText,
    getTextSize,
    pluralize,
    truncateText,
    levenshteinDistance,
    toTitleCase,
}
